import { FormEvent, type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  formatHttpErrorDetails,
  formatThrownErrorDetails,
  friendlyHttpFailureSummary,
  friendlyNetworkErrorMessage,
} from '../api/httpErrorFormat';
import { extractAccessTokenFromLoginJson } from '../api/authLoginResponse';
import { isSuccessfulHttpStatus } from '../api/apiFetch';
import { clearRefreshUnavailable404, extractRefreshTokenFromUnknown } from '../api/authRefresh';
import { fetchWithDevProxyFallback } from '../api/fetchWithDevProxyFallback';
import { persistStoredRefreshToken } from '../auth/sessionToken';
import { persistIdentifierForLoginOption } from './lastLoginStorage';
import { persistLastSignInMethod } from './lastSignInMethod';
import { clearPersistedLoginAttempt, persistLoginAttempt } from '../utils/authAttemptStorage';
import { useI18n } from '../i18n';
import { localizeServerMessage } from '../i18n/localizeServerMessage';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import {
  getInitialLoginForm,
  getLastLoginState,
  LOGIN_META,
  loginFormPayloadForOption,
  type LoginOption,
} from './loginConstants';

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export type UseLoginFormOptions = {
  /** Portal login: credentials always visible; submit does not require opening the dev toolbar form first. */
  portalMode?: boolean;
  /** Lock sign-in to one login type (e.g. private client on the main auth portal). */
  fixedLoginOption?: LoginOption;
  /** Restrict the login-type picker on specialized auth pages. */
  allowedLoginOptions?: readonly LoginOption[];
};

function resolveInitialLoginState(options: UseLoginFormOptions): {
  option: LoginOption;
  payload: { identifier: string; password: string };
} {
  const { fixedLoginOption, allowedLoginOptions } = options;
  if (fixedLoginOption) {
    return {
      option: fixedLoginOption,
      payload: loginFormPayloadForOption(fixedLoginOption),
    };
  }
  if (allowedLoginOptions?.length) {
    const last = getLastLoginState();
    const option =
      last && allowedLoginOptions.includes(last.option)
        ? last.option
        : allowedLoginOptions[0];
    return {
      option,
      payload: loginFormPayloadForOption(option),
    };
  }
  return getInitialLoginForm();
}

export function useLoginForm(options: UseLoginFormOptions = {}) {
  const { portalMode = false, fixedLoginOption, allowedLoginOptions } = options;
  const { t } = useI18n();
  const { applyLoginSuccess, isLoggedIn } = useAuth();
  const { setView } = useAppNavigation();
  const initialLogin = resolveInitialLoginState(options);
  const [selectedOption, setSelectedOption] = useState<LoginOption>(initialLogin.option);
  const [payload, setPayload] = useState(initialLogin.payload);
  const [formOpen, setFormOpen] = useState(portalMode);
  const [inputsFolded, setInputsFolded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loginErrorFriendly, setLoginErrorFriendly] = useState<string | null>(null);
  const [loginErrorDetails, setLoginErrorDetails] = useState<string | null>(null);
  const [loginFieldKey, setLoginFieldKey] = useState(0);
  const wasLoggedInRef = useRef(isLoggedIn);

  const loginBlockedTitle = t('login.blockedSignInElse');

  useEffect(() => {
    if (isLoggedIn) {
      setFormOpen(portalMode);
      setInputsFolded(false);
    }
  }, [isLoggedIn, portalMode]);

  useEffect(() => {
    if (wasLoggedInRef.current && !isLoggedIn) {
      const restored = getLastLoginState();
      if (restored) {
        setSelectedOption(restored.option);
        setPayload(loginFormPayloadForOption(restored.option));
      } else {
        setPayload(loginFormPayloadForOption(selectedOption));
      }
      setLoginFieldKey((k) => k + 1);
    }
    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, selectedOption]);

  const onOptionChange = (option: LoginOption) => {
    if (fixedLoginOption) {
      return;
    }
    if (allowedLoginOptions && !allowedLoginOptions.includes(option)) {
      return;
    }
    setSelectedOption(option);
    setPayload(loginFormPayloadForOption(option));
    setInputsFolded(false);
    setLoginErrorFriendly(null);
    setLoginErrorDetails(null);
  };

  const onClearFields = useCallback(() => {
    clearPersistedLoginAttempt(selectedOption);
    setPayload(deepClone(LOGIN_META[selectedOption].emptyPayload));
    setLoginFieldKey((k) => k + 1);
    setInputsFolded(false);
    setLoginErrorFriendly(null);
    setLoginErrorDetails(null);
  }, [selectedOption]);

  const onClearButtonClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClearFields();
    },
    [onClearFields],
  );

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isLoggedIn) {
      return;
    }
    if (!portalMode && !formOpen) {
      setFormOpen(true);
      return;
    }

    const identifier = payload.identifier.trim();
    const password = payload.password;
    if (!identifier || !password) {
      setLoginErrorFriendly(t('login.credentialsRequired'));
      setLoginErrorDetails(null);
      return;
    }

    setSubmitting(true);
    setLoginErrorFriendly(null);
    setLoginErrorDetails(null);

    persistLoginAttempt(selectedOption, payload);

    const body: Record<string, unknown> = {
      identifier,
      password,
    };

    const apiPathTail = LOGIN_META[selectedOption].path;

    try {
      const { response, urlUsed } = await fetchWithDevProxyFallback(
        apiPathTail,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
        'login',
      );

      const responseText = await response.text();

      if (isSuccessfulHttpStatus(response.status)) {
        const submittedId = payload.identifier.trim();
        if (submittedId) {
          persistIdentifierForLoginOption(selectedOption, submittedId);
        }
        persistLastSignInMethod('password');
        if (responseText.trim()) {
          try {
            const parsed: unknown = JSON.parse(responseText);
            const at = extractAccessTokenFromLoginJson(parsed);
            if (at) {
              applyLoginSuccess(parsed, at, payload.identifier, selectedOption);
              const rt = extractRefreshTokenFromUnknown(parsed);
              if (rt) {
                persistStoredRefreshToken(rt);
                clearRefreshUnavailable404();
              }
              setPayload((p) => ({ ...p, password: '' }));
              setLoginFieldKey((k) => k + 1);
              setView('home', { replace: true });
            } else {
              setLoginErrorFriendly(
                'Sign-in succeeded but the response did not include an accessToken. Check the login API JSON shape.',
              );
              setLoginErrorDetails(
                formatHttpErrorDetails({
                  requestUrl: urlUsed,
                  requestPayload: body,
                  response,
                  responseText,
                }),
              );
            }
          } catch {
            setLoginErrorFriendly(t('login.signInNoToken'));
            setLoginErrorDetails(
              formatHttpErrorDetails({
                requestUrl: urlUsed,
                requestPayload: body,
                response,
                responseText,
              }),
            );
          }
        } else {
          setLoginErrorFriendly(t('login.signInEmptyBody', { status: response.status }));
        }
      } else {
        setLoginErrorFriendly(
          localizeServerMessage(
            friendlyHttpFailureSummary(response.status, response.statusText, responseText),
            t,
          ),
        );
        setLoginErrorDetails(
          formatHttpErrorDetails({
            requestUrl: urlUsed,
            requestPayload: body,
            response,
            responseText,
          }),
        );
      }
    } catch (submitError) {
      setLoginErrorFriendly(friendlyNetworkErrorMessage(submitError));
      setLoginErrorDetails(formatThrownErrorDetails(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return {
    t,
    isLoggedIn,
    loginBlockedTitle,
    selectedOption,
    fixedLoginOption,
    allowedLoginOptions,
    onOptionChange,
    payload,
    setPayload,
    formOpen,
    setFormOpen,
    inputsFolded,
    setInputsFolded,
    submitting,
    loginErrorFriendly,
    loginErrorDetails,
    loginFieldKey,
    onClearButtonClick,
    onSubmit,
    onClearFields,
  };
}
